import { FormEvent, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';

interface StaffLoginCodeFormProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  error?: string | null;
  isLoading?: boolean;
}

export function StaffLoginCodeForm({
  email,
  onVerify,
  onResend,
  onBack,
  error,
  isLoading = false,
}: StaffLoginCodeFormProps) {
  const [code, setCode] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onVerify(code);
  };

  const handleResend = async () => {
    setIsResending(true);
    setResendSuccess(false);
    try {
      await onResend();
      setResendSuccess(true);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <div className="text-center mb-6">
        <h1 className="text-xl font-semibold mb-2">Check your email</h1>
        <p className="text-sm text-foreground-secondary">
          Enter the 6-digit code sent to <span className="text-foreground font-medium">{email}</span>
        </p>
      </div>

      <div className="mb-6 p-3 bg-accent/10 border border-accent/20 rounded-lg flex gap-3">
        <Mail className="w-5 h-5 text-accent flex-shrink-0" />
        <p className="text-xs text-foreground-secondary">
          Enter the verification code we sent to your email to finish signing in.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-error/10 text-error text-sm rounded-lg">{error}</div>
      )}

      {resendSuccess && (
        <div className="mb-4 p-3 bg-success/10 text-success text-sm rounded-lg">
          A new code has been sent.
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="login-code" className="label">
            Verification code
          </label>
          <input
            id="login-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="input text-center text-2xl tracking-widest font-mono"
            placeholder="000000"
            maxLength={6}
            required
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={isLoading || code.length !== 6}
          className="btn-primary w-full py-2.5"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify and continue'
          )}
        </button>
      </form>

      <div className="mt-6 pt-6 border-t border-border text-center space-y-3">
        <button
          type="button"
          onClick={handleResend}
          disabled={isResending}
          className="text-sm text-accent hover:text-accent-hover"
        >
          {isResending ? 'Sending...' : 'Resend code'}
        </button>
        <div>
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-foreground-secondary hover:text-foreground"
          >
            Back to sign in
          </button>
        </div>
      </div>
    </>
  );
}
